import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  PasswordInput,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { CheckCircle, Edit2, Plus, Search, Trash2, Unlock, User, XCircle } from 'lucide-react'
import { type SatPlan, satPlansApi } from '../../api/satPlans'
import { CreateUserPayload, UpdateUserPayload, UserItem, usersApi } from '../../api/users'
import { useAuth } from '../../context/AuthContext'

const PAGE_SIZE = 20

type StatusFilter = 'all' | 'active' | 'inactive'

interface UserFormValues {
  email: string
  password: string
  user_name: string
  first_name: string
  last_name: string
  role: string
  phone: string
  timezone: string
  is_active: boolean
  is_verified: boolean
  plan_code: string
  subscription_end_date: string
}

const defaultValues: UserFormValues = {
  email: '',
  password: '',
  user_name: '',
  first_name: '',
  last_name: '',
  role: 'student',
  phone: '',
  timezone: 'UTC',
  is_active: true,
  is_verified: true,
  plan_code: '',
  subscription_end_date: '',
}

function formatDate(value: string | null) {
  if (!value) return 'Never'
  return new Date(value).toLocaleDateString()
}

function formatDateTimeInput(value: string | null) {
  if (!value) return ''
  const date = new Date(value)
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toApiDateTime(value: string) {
  return value.trim() ? new Date(value).toISOString() : undefined
}

function getDisplayName(user: UserItem) {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return fullName || user.user_name
}

export function UserManagement() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<UserItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchText, 350)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserItem | null>(null)
  const [unlockingUserId, setUnlockingUserId] = useState<number | null>(null)
  const [satPlans, setSatPlans] = useState<SatPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)

  const form = useForm<UserFormValues>({
    initialValues: defaultValues,
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value.trim()) ? null : 'Enter a valid email'),
      password: (value) => {
        if (editingUser) {
          return value.length === 0 || value.length >= 6 ? null : 'Password must be at least 6 characters'
        }
        return value.length >= 6 ? null : 'Password must be at least 6 characters'
      },
      user_name: (value) => (value.trim().length >= 2 ? null : 'Username is required'),
      role: (value) => (value ? null : 'Role is required'),
    },
  })

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const response = await usersApi.list({
        search: debouncedSearch || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      })
      setUsers(response.data.items)
      setTotal(response.data.total)
    } catch (error: any) {
      setUsers([])
      setTotal(0)
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to load users',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, statusFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, statusFilter])

  useEffect(() => {
    let cancelled = false
    setPlansLoading(true)
    satPlansApi
      .list()
      .then((res) => {
        if (!cancelled) setSatPlans(res.data)
      })
      .catch(() => {
        if (!cancelled) setSatPlans([])
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const planCodeSelectData = useMemo(() => {
    const fromCatalog = satPlans.map((p) => ({
      value: p.plan_code,
      label: `${p.name} (${p.plan_code})`,
    }))
    const code = (form.values.plan_code ?? '').trim()
    if (editingUser && code && !fromCatalog.some((o) => o.value === code)) {
      return [
        { value: '', label: 'No plan' },
        { value: code, label: `${code} (not in catalog)` },
        ...fromCatalog,
      ]
    }
    return [{ value: '', label: 'No plan' }, ...fromCatalog]
  }, [satPlans, editingUser, form.values.plan_code])

  const openCreateModal = () => {
    setEditingUser(null)
    form.setValues(defaultValues)
    form.resetDirty(defaultValues)
    setModalOpen(true)
  }

  const openEditModal = (user: UserItem) => {
    setEditingUser(user)
    const values: UserFormValues = {
      email: user.email,
      password: '',
      user_name: user.user_name,
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      role: user.role,
      phone: user.phone ?? '',
      timezone: user.timezone || 'UTC',
      is_active: user.is_active,
      is_verified: user.is_verified,
      plan_code: user.plan_code ?? '',
      subscription_end_date: formatDateTimeInput(user.subscription_end_date),
    }
    form.setValues(values)
    form.resetDirty(values)
    setModalOpen(true)
  }

  const refreshAfterMutation = async () => {
    if (page > 1 && users.length === 1) {
      setPage((currentPage) => currentPage - 1)
      return
    }
    await fetchUsers()
  }

  const handleSubmit = async (values: UserFormValues) => {
    setSaving(true)
    try {
      if (editingUser) {
        const payload: UpdateUserPayload = {
          email: values.email.trim(),
          user_name: values.user_name.trim(),
          first_name: values.first_name.trim() || undefined,
          last_name: values.last_name.trim() || undefined,
          role: values.role,
          phone: values.phone.trim() || undefined,
          timezone: values.timezone.trim() || 'UTC',
          is_active: values.is_active,
          is_verified: values.is_verified,
          plan_code: values.plan_code.trim(),
          subscription_end_date: toApiDateTime(values.subscription_end_date),
        }
        if (values.password.trim()) {
          payload.password = values.password.trim()
        }
        await usersApi.update(editingUser.id, payload)
        notifications.show({
          title: 'Updated',
          message: 'User updated successfully.',
          color: 'green',
        })
      } else {
        const payload: CreateUserPayload = {
          email: values.email.trim(),
          password: values.password.trim(),
          user_name: values.user_name.trim(),
          first_name: values.first_name.trim() || undefined,
          last_name: values.last_name.trim() || undefined,
          role: values.role,
          phone: values.phone.trim() || undefined,
          timezone: values.timezone.trim() || 'UTC',
          is_active: values.is_active,
          is_verified: values.is_verified,
          plan_code: values.plan_code.trim(),
          subscription_end_date: toApiDateTime(values.subscription_end_date),
        }
        await usersApi.create(payload)
        notifications.show({
          title: 'Created',
          message: 'User created successfully.',
          color: 'green',
        })
      }

      setModalOpen(false)
      await refreshAfterMutation()
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to save user',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (user: UserItem) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active })
      notifications.show({
        title: user.is_active ? 'User deactivated' : 'User reactivated',
        message: `${getDisplayName(user)} has been ${user.is_active ? 'deactivated' : 'reactivated'}.`,
        color: user.is_active ? 'orange' : 'green',
      })
      await fetchUsers()
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to update user status',
        color: 'red',
      })
    }
  }

  const handleDelete = async (user: UserItem) => {
    if (!window.confirm(`Delete ${getDisplayName(user)}? This cannot be undone.`)) return

    try {
      await usersApi.delete(user.id)
      notifications.show({
        title: 'Deleted',
        message: 'User deleted successfully.',
        color: 'green',
      })
      await refreshAfterMutation()
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to delete user',
        color: 'red',
      })
    }
  }

  const handleUnlockSession = async (user: UserItem) => {
    const confirmMsg =
      `Unlock ${getDisplayName(user)}?\n\n` +
      `This ends every active login session for this account so they can sign in ` +
      `from a new device. Use this when a user forgot to sign out elsewhere.`
    if (!window.confirm(confirmMsg)) return

    setUnlockingUserId(user.id)
    try {
      const { data } = await usersApi.unlockSession(user.id)
      notifications.show({
        title: data.ended_count > 0 ? 'Sessions unlocked' : 'Nothing to unlock',
        message: data.message,
        color: data.ended_count > 0 ? 'green' : 'gray',
      })
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.detail || 'Failed to unlock user session',
        color: 'red',
      })
    } finally {
      setUnlockingUserId(null)
    }
  }

  const rows = useMemo(
    () =>
      users.map((user) => {
        const isCurrentUser = currentUser?.id === user.id

        return (
          <Table.Tr key={user.id}>
            <Table.Td>
              <Stack gap={2}>
                <Text fw={600} c="var(--sat-text)">{getDisplayName(user)}</Text>
                <Text size="sm" c="dimmed">@{user.user_name}</Text>
              </Stack>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{user.email}</Text>
            </Table.Td>
            <Table.Td>
              <Badge variant="light" color={user.role === 'god' ? 'grape' : user.role === 'tutor' ? 'blue' : 'teal'}>
                {user.role}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{user.plan_code || 'None'}</Text>
            </Table.Td>
            <Table.Td>
              <Group gap={6}>
                <Badge variant="light" color={user.is_active ? 'green' : 'gray'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <Badge variant="dot" color={user.is_verified ? 'green' : 'orange'}>
                  {user.is_verified ? 'Verified' : 'Unverified'}
                </Badge>
              </Group>
            </Table.Td>
            <Table.Td>
              <Stack gap={2}>
                <Text size="sm">
                  Ends: {formatDate(user.subscription_end_date)}
                </Text>
              </Stack>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDate(user.create_datetime)}</Text>
            </Table.Td>
            <Table.Td>
              <Group gap="xs" justify="flex-end" wrap="nowrap">
                <ActionIcon
                  variant="light"
                  color="blue"
                  onClick={() => openEditModal(user)}
                  aria-label={`Edit ${user.user_name}`}
                >
                  <Edit2 size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="light"
                  color={user.is_active ? 'orange' : 'green'}
                  onClick={() => handleToggleActive(user)}
                  aria-label={user.is_active ? `Deactivate ${user.user_name}` : `Activate ${user.user_name}`}
                  disabled={isCurrentUser}
                >
                  {user.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                </ActionIcon>
                <ActionIcon
                  variant="light"
                  color="yellow"
                  onClick={() => handleUnlockSession(user)}
                  aria-label={`Unlock sessions for ${user.user_name}`}
                  title="Unlock active session (user forgot to sign out on another device)"
                  loading={unlockingUserId === user.id}
                  disabled={isCurrentUser}
                >
                  <Unlock size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => handleDelete(user)}
                  aria-label={`Delete ${user.user_name}`}
                  disabled={isCurrentUser}
                >
                  <Trash2 size={16} />
                </ActionIcon>
              </Group>
            </Table.Td>
          </Table.Tr>
        )
      }),
    [currentUser?.id, users, unlockingUserId]
  )

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="end">
          <div>
            <Title order={2} c="var(--sat-text)">User Management</Title>
            <Text c="var(--sat-text-muted)" mt={6}>
              Manage platform users with quick search, status filters, and admin controls.
            </Text>
          </div>
          <Button leftSection={<Plus size={16} />} radius="xl" color="grape" onClick={openCreateModal}>
            Add user
          </Button>
        </Group>

        <Paper withBorder radius="xl" p="lg" style={{ background: 'var(--sat-surface)' }}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TextInput
              label="Search"
              placeholder="Filter by email or name"
              leftSection={<Search size={16} />}
              value={searchText}
              onChange={(event) => setSearchText(event.currentTarget.value)}
            />
            <Select
              label="Status"
              value={statusFilter}
              onChange={(value) => setStatusFilter((value as StatusFilter) || 'all')}
              data={[
                { value: 'all', label: 'All users' },
                { value: 'active', label: 'Active only' },
                { value: 'inactive', label: 'Inactive only' },
              ]}
            />
          </SimpleGrid>
        </Paper>

        <Paper withBorder radius="xl" p="lg" style={{ background: 'var(--sat-surface)' }}>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Badge size="lg" radius="xl" variant="light" color="grape">{total}</Badge>
              <Text fw={600}>users found</Text>
            </Group>
            <Text size="sm" c="dimmed">20 items per page</Text>
          </Group>

          {loading ? (
            <Group justify="center" py="xl">
              <Loader color="grape" />
            </Group>
          ) : users.length === 0 ? (
            <Stack align="center" gap="xs" py="xl">
              <User size={28} color="var(--sat-text-muted)" />
              <Text fw={600}>No users match your filters.</Text>
              <Text size="sm" c="dimmed">Try a different name, email, or status filter.</Text>
            </Stack>
          ) : (
            <ScrollArea>
              <Table highlightOnHover verticalSpacing="md" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Plan Code</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Subscription</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </ScrollArea>
          )}

          {totalPages > 1 && (
            <Group justify="center" mt="lg">
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                color="grape"
                radius="xl"
                withEdges
              />
            </Group>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingUser ? 'Edit user' : 'Create user'}
        centered
        radius="xl"
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                label="Email"
                placeholder="user@example.com"
                required
                {...form.getInputProps('email')}
              />
              <TextInput
                label="Username"
                placeholder="Display username"
                required
                {...form.getInputProps('user_name')}
              />
              <TextInput
                label="First name"
                placeholder="Optional"
                {...form.getInputProps('first_name')}
              />
              <TextInput
                label="Last name"
                placeholder="Optional"
                {...form.getInputProps('last_name')}
              />
              <Select
                label="Role"
                data={[
                  { value: 'student', label: 'Student' },
                  { value: 'tutor', label: 'Tutor' },
                  { value: 'god', label: 'God' },
                ]}
                required
                {...form.getInputProps('role')}
              />
              <TextInput
                label="Phone"
                placeholder="Optional"
                {...form.getInputProps('phone')}
              />
              <TextInput
                label="Timezone"
                placeholder="UTC"
                {...form.getInputProps('timezone')}
              />
              {editingUser ? (
                <Select
                  label="Plan code"
                  placeholder={plansLoading ? 'Loading plans…' : 'Select a plan'}
                  data={planCodeSelectData}
                  disabled={plansLoading}
                  searchable
                  {...form.getInputProps('plan_code')}
                />
              ) : (
                <TextInput
                  label="Plan code"
                  placeholder="Set plan code manually"
                  {...form.getInputProps('plan_code')}
                />
              )}
              <TextInput
                label="Subscription end"
                type="datetime-local"
                {...form.getInputProps('subscription_end_date')}
              />
              <PasswordInput
                label={editingUser ? 'New password' : 'Password'}
                placeholder={editingUser ? 'Leave blank to keep current password' : 'At least 6 characters'}
                required={!editingUser}
                {...form.getInputProps('password')}
              />
            </SimpleGrid>

            <Group grow>
              <Switch
                label="Account active"
                checked={form.values.is_active}
                onChange={(event) => form.setFieldValue('is_active', event.currentTarget.checked)}
              />
              <Switch
                label="Email verified"
                checked={form.values.is_verified}
                onChange={(event) => form.setFieldValue('is_verified', event.currentTarget.checked)}
              />
            </Group>

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" color="grape" loading={saving}>
                {editingUser ? 'Save changes' : 'Create user'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  )
}
